import { createContext } from "react";
import { makeAutoObservable, observable } from "mobx";
import * as Cookies from "js-cookie";
import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { SessionData } from "../interfaces/session";
import { UsersServiceInstance } from "./users";
import { CognitoTokenRefreshResponse, CognitoTokenResponse } from "../shared/interfaces";
import { getEmptyUserRights, parseUserRights, UserRightsType } from "../utils/groups";
import { normalizeOperatorEmail } from "../utils/operatorEmail";
import { getMyAccess } from "../axios/routes/access";
import {
  applyOperatorRoleFlagsFromGroups,
  applyOperatorRoleFlagsFromMongoRoles,
  defaultLandingRouteFromGroups,
} from "../utils/operatorRoles";
import { isOperatorProfileComplete } from "../utils/operatorProfileComplete";
import { Routes } from "../utils/routes";
import {
  clearOperatorSessionTokens,
  getOperatorAccessToken,
  getOperatorEmailCookie,
  getOperatorIdToken,
  getOperatorRefreshToken,
  persistOperatorSessionTokens,
} from "../utils/operatorSessionTokens";

class SessionService {
  /** Refresh IdToken this far before Cognito `exp` (Downloads / Videoboard image APIs). */
  private static readonly ID_TOKEN_REFRESH_SKEW_MS = 5 * 60 * 1000;

  private idTokenRefreshInFlight: Promise<string | undefined> | null = null;

  @observable EmailAddress = null;

  @observable UserGroups: string[] = [];

  @observable UserRights: UserRightsType | null = null;

  @observable isSuperAdmin = false;

  @observable isOwnerAdmin = false;

  @observable isIndexingOperator = false;

  @observable isTagboardOperator = false;

  /** True when Mongo roles include operator / superAdmin / ownerAdmin (not indexing-only). */
  @observable hasGeneralOperatorAccess = false;

  /** Indexing without Games/Schedule operator access. */
  @observable isIndexingOnlyOperator = false;

  /** Tagboard without general operator access. */
  @observable isTagboardOnlyOperator = false;

  @observable accessibleTeamIds: string[] = [];

  @observable accessibleTeamNames: string[] = [];

  /**
   * null = not loaded yet; false = must finish Account page before operating.
   */
  @observable profileComplete: boolean | null = null;

  /**
   * Read-only Schedule for stadium operators and administrator operators.
   * The Operator role does not get the Schedule tab.
   */
  get canViewScheduleNav(): boolean {
    if (
      !this.EmailAddress
      || this.isSuperAdmin
      || this.isIndexingOnlyOperator
      || this.isTagboardOnlyOperator
    ) {
      return false;
    }
    const r = this.UserRights;
    if (!r) return false;
    if (r.isOperator && !r.isAdmininstrator) return false;
    return r.isOperator || (r.operatorForStadiums?.length ?? 0) > 0;
  }

  get defaultLandingRoute(): string {
    if (this.profileComplete === false) {
      return Routes.ACCOUNT;
    }
    if (this.isIndexingOnlyOperator) {
      return Routes.INDEXING;
    }
    if (this.isTagboardOnlyOperator) {
      return Routes.DOWNLOADS;
    }
    return Routes.GAMES;
  }

  constructor() {
    makeAutoObservable(this);

    this.init();
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private init = async () => {}

  private applyOperatorRoleFlags(
    groups: string[],
    mongoRole?: string | null,
    mongoRoles?: unknown,
  ): void {
    const fromGroups = applyOperatorRoleFlagsFromGroups(groups);
    this.isOwnerAdmin = fromGroups.isOwnerAdmin;
    this.isSuperAdmin = fromGroups.isSuperAdmin;
    this.isIndexingOperator = fromGroups.isIndexingOperator;
    this.isTagboardOperator = fromGroups.isTagboardOperator;
    const inOperatorsGroup = groups.some(
      (g) => String(g).toLowerCase() === "operators",
    );
    this.hasGeneralOperatorAccess = fromGroups.isSuperAdmin
      || fromGroups.isOwnerAdmin
      || inOperatorsGroup;

    if (mongoRole != null || mongoRoles != null) {
      const fromMongo = applyOperatorRoleFlagsFromMongoRoles(mongoRoles, mongoRole);
      this.isOwnerAdmin = this.isOwnerAdmin || fromMongo.isOwnerAdmin;
      this.isSuperAdmin = this.isSuperAdmin || fromMongo.isSuperAdmin;
      this.isIndexingOperator = this.isIndexingOperator || fromMongo.isIndexingOperator;
      this.isTagboardOperator = this.isTagboardOperator || fromMongo.isTagboardOperator;
      this.hasGeneralOperatorAccess = fromMongo.hasGeneralOperatorAccess
        || this.isSuperAdmin;
      this.isIndexingOnlyOperator = fromMongo.isIndexingOnly && !this.isSuperAdmin;
      this.isTagboardOnlyOperator = fromMongo.isTagboardOnly && !this.isSuperAdmin;
      return;
    }

    this.isIndexingOnlyOperator = this.isIndexingOperator
      && !this.isSuperAdmin
      && !this.hasGeneralOperatorAccess;
    this.isTagboardOnlyOperator = this.isTagboardOperator
      && !this.isSuperAdmin
      && !this.isIndexingOperator
      && !this.hasGeneralOperatorAccess;
  }

  public static checkTokenExpiration = async (sessionData: SessionData) : Promise<SessionData> => {
    return new Promise((resolve, reject) => {
      const cognitoRefreshToken = new CognitoRefreshToken({
        RefreshToken: sessionData.RefreshToken,
      });
      const cognitoAccessToken = new CognitoAccessToken({ AccessToken: sessionData.AccessToken });
      const cognitoIdToken = new CognitoIdToken({ IdToken: sessionData.IdToken });
      const cachedSession = new CognitoUserSession({
        AccessToken: cognitoAccessToken,
        IdToken: cognitoIdToken,
        RefreshToken: cognitoRefreshToken,
      });

      const accessTokenExpirationDate = new Date(cognitoAccessToken.getExpiration() * 1000);
      const idTokenExpirationDate = new Date(cognitoIdToken.getExpiration() * 1000);
      const now = new Date();
      console.log(`SessionService.checkTokenExpiration() - AccessToken ${(accessTokenExpirationDate > now) ? "VALID until " : "EXPIRED at "}`, accessTokenExpirationDate);
      console.log(`SessionService.checkTokenExpiration() - IdToken ${(idTokenExpirationDate > now) ? "VALID until " : "EXPIRED at "}`, idTokenExpirationDate);

      if ((cachedSession.isValid() === true)
       && (idTokenExpirationDate > now)
       && (accessTokenExpirationDate > now)) {
        // cached session tokens are valid, and not expired
        resolve(sessionData);
      } else {
        // cached session tokens are either invalid or expired
        // TODO: only refresh the authentication tokens if the user has explicitly said
        //       "remember me"
        UsersServiceInstance.refreshCognitoTokens(sessionData).then(
          (response: CognitoTokenRefreshResponse) => {
            console.log("UsersServiceInstance.refreshCognitoTokens() returned ", response);
            if (response == null) {
              reject({ Error: "UsersServiceInstance.refreshCognitoTokens() returned null" });
            } else {
              resolve({
                AccessToken: response.AccessToken,
                IdToken: response.IdToken,
                RefreshToken: response.RefreshToken,
                EmailAddress: normalizeOperatorEmail(sessionData.EmailAddress),
              });
            }
          },
        ).catch(
          (error) => {
            console.log("Error refreshing tokens:", error);
            reject(error);
          },
        );
      }
    });
  };

  public setSession = (data: SessionData) => {
    const email = normalizeOperatorEmail(data.EmailAddress);
    console.log("setSession", { ...data });
    this.EmailAddress = email || null;

    persistOperatorSessionTokens({
      AccessToken: data.AccessToken,
      IdToken: data.IdToken,
      RefreshToken: data.RefreshToken,
      EmailAddress: email,
    });

    this.applyGroupsFromIdToken(data.IdToken);
  };

  /**
   * Sync decode of Cognito groups from IdToken. Must run before any await in getSession so
   * socket events (e.g. new moment) see isSuperAdmin / rights immediately; otherwise super
   * admins were gated by isOperatorAssignedToGame until getUserRights finished.
   */
  private applyGroupsFromIdToken(idToken: string | undefined | null): void {
    let groups: string[] = [];
    try {
      if (idToken) {
        const cognitoIdToken = new CognitoIdToken({ IdToken: idToken });
        const payload = cognitoIdToken.decodePayload();
        groups = payload["cognito:groups"] || [];
      }
    } catch (e) {
      console.warn("Failed to decode id_token groups", e);
    }

    this.UserGroups = groups;
    this.UserRights = parseUserRights(groups);
    this.applyOperatorRoleFlags(groups);
  }

  private getUserRights = async () => {
    this.applyGroupsFromIdToken(getOperatorIdToken());

    try {
      if (this.EmailAddress) {
        const access = await getMyAccess(this.EmailAddress);
        this.accessibleTeamIds = access.teamIds || [];
        this.accessibleTeamNames = access.teamNames || [];
        this.applyOperatorRoleFlags(this.UserGroups, access.role, access.roles);
        this.applyProfileCompleteness(access);
      }
    } catch (e) {
      console.warn("Failed to fetch access record", e);
      this.accessibleTeamIds = [];
      this.accessibleTeamNames = [];
      this.profileComplete = false;
    }
  }

  private applyProfileCompleteness = (access: {
    firstName?: string;
    name?: string;
    phoneNumber?: string;
    avatarUrl?: string;
    hasAvatar?: boolean;
    addressLine1?: string;
    addressCity?: string;
    addressState?: string;
    addressZip?: string;
    profileComplete?: boolean;
    found?: boolean;
  }): void => {
    if (typeof access.profileComplete === "boolean") {
      this.profileComplete = access.profileComplete;
      return;
    }
    if (access.found === false) {
      this.profileComplete = false;
      return;
    }
    this.profileComplete = isOperatorProfileComplete(access);
  }

  /** Re-fetch /me to refresh whether required account fields are filled. */
  public refreshProfileCompleteness = async (): Promise<boolean> => {
    if (!this.EmailAddress) {
      this.profileComplete = false;
      return false;
    }
    try {
      const access = await getMyAccess(this.EmailAddress);
      this.applyProfileCompleteness(access);
    } catch {
      this.profileComplete = false;
    }
    return this.profileComplete === true;
  }

  public markProfileCompleteLocally = (complete: boolean): void => {
    this.profileComplete = complete;
  }

  public getCookie = async (name: string): Promise<string> => {
    if (name === "SessionIdToken") return getOperatorIdToken() || "";
    if (name === "SessionAccessToken") return getOperatorAccessToken() || "";
    if (name === "SessionRefreshToken") return getOperatorRefreshToken() || "";
    if (name === "SessionEmailAddress") return getOperatorEmailCookie() || "";
    // Non-auth cookies (e.g. provisioned concurrency prefs)
    return Cookies.get(name) || "";
  }

  public setCookie = async (name: string, value: string) => {
    if (
      name === "SessionAccessToken"
      || name === "SessionIdToken"
      || name === "SessionRefreshToken"
      || name === "SessionEmailAddress"
    ) {
      persistOperatorSessionTokens({
        AccessToken: name === "SessionAccessToken" ? value : getOperatorAccessToken(),
        IdToken: name === "SessionIdToken" ? value : getOperatorIdToken(),
        RefreshToken: name === "SessionRefreshToken" ? value : getOperatorRefreshToken(),
        EmailAddress: name === "SessionEmailAddress" ? value : getOperatorEmailCookie(),
      });
      return;
    }
    Cookies.set(name, value);
  }

  /**
   * Ensure Cognito IdToken is usable for Downloads / Videoboard image APIs.
   * Refreshes when expired or within {@link ID_TOKEN_REFRESH_SKEW_MS} of expiry so
   * long-lived operator tabs do not hit "jwt expired" on /operator/image/*.
   */
  public ensureFreshIdToken = async (opts?: { force?: boolean }): Promise<string | undefined> => {
    if (this.idTokenRefreshInFlight) {
      return this.idTokenRefreshInFlight;
    }
    this.idTokenRefreshInFlight = this.ensureFreshIdTokenInternal(opts?.force === true)
      .finally(() => {
        this.idTokenRefreshInFlight = null;
      });
    return this.idTokenRefreshInFlight;
  };

  private ensureFreshIdTokenInternal = async (force: boolean): Promise<string | undefined> => {
    const idToken = getOperatorIdToken();
    const accessToken = getOperatorAccessToken();
    const refreshToken = getOperatorRefreshToken();
    const email = normalizeOperatorEmail(getOperatorEmailCookie());
    if (!idToken) return undefined;
    if (!accessToken || !refreshToken) return idToken;

    let needsRefresh = force;
    if (!needsRefresh) {
      try {
        const cognitoIdToken = new CognitoIdToken({ IdToken: idToken });
        const expMs = cognitoIdToken.getExpiration() * 1000;
        needsRefresh = expMs <= Date.now() + SessionService.ID_TOKEN_REFRESH_SKEW_MS;
      } catch {
        needsRefresh = true;
      }
    }
    if (!needsRefresh) return idToken;

    try {
      const response = await UsersServiceInstance.refreshCognitoTokens({
        IdToken: idToken,
        RefreshToken: refreshToken,
      });
      if (!response?.IdToken) return getOperatorIdToken();
      this.setSession({
        AccessToken: response.AccessToken,
        IdToken: response.IdToken,
        RefreshToken: response.RefreshToken || refreshToken,
        EmailAddress: email,
      });
      return response.IdToken;
    } catch (err) {
      console.warn("[SessionService.ensureFreshIdToken] Cognito refresh failed", err);
      return getOperatorIdToken();
    }
  };

  public getSession = async (): Promise<SessionData> => {
    console.log("try get session");
    const sessionData : SessionData = {
      IdToken: getOperatorIdToken(),
      AccessToken: getOperatorAccessToken(),
      RefreshToken: getOperatorRefreshToken(),
      EmailAddress: normalizeOperatorEmail(getOperatorEmailCookie()),
      // eslint-disable-next-line no-empty
    };
    // Backfill session/local storage when cookies still hold tokens (older sessions).
    if (sessionData.AccessToken || sessionData.IdToken) {
      persistOperatorSessionTokens(sessionData);
    }
    console.log("sessionData=", { ...sessionData });
    this.EmailAddress = sessionData.EmailAddress || null;
    this.applyGroupsFromIdToken(sessionData.IdToken);

    let updatedSessionData = null;
    if (sessionData.AccessToken) {
      // try {
      updatedSessionData = await SessionService.checkTokenExpiration(sessionData);
      if (sessionData.AccessToken !== updatedSessionData.AccessToken) {
        this.setSession(updatedSessionData);
      }
      // } catch (err) {
      //  updatedSessionData = null;
      // }

      await this.getUserRights();
      console.log(this.UserRights);
    }

    return updatedSessionData;
  }

  public clearSession = async () => {
    console.log("clearSession");
    this.EmailAddress = null;
    this.isSuperAdmin = false;
    this.isOwnerAdmin = false;
    this.isIndexingOperator = false;
    this.isTagboardOperator = false;
    this.hasGeneralOperatorAccess = false;
    this.isIndexingOnlyOperator = false;
    this.isTagboardOnlyOperator = false;
    this.accessibleTeamIds = [];
    this.accessibleTeamNames = [];
    this.profileComplete = null;

    clearOperatorSessionTokens();
  }

  public static landingRouteFromGroups(groups: string[]): string {
    return defaultLandingRouteFromGroups(groups);
  }
}

export const SessionServiceInstance = new SessionService();

export const SessionContext = createContext(SessionServiceInstance);
